import React, { useCallback, useEffect, useState } from 'react'
import { getWindow, fetch } from '@ztechapps/common.utils'

export type UseRecaptchaProps = { recaptchaSiteKey: string; url?: string }
export const useRecaptcha = ({ recaptchaSiteKey, url = '/api/recaptcha' }: UseRecaptchaProps) => {
  const [readyCaptcha, setReadyCaptcha] = React.useState(false)

  useEffect(() => {
    if (!recaptchaSiteKey) return

    const load = (callback) => {
      const existingScript = getWindow()?.document.getElementById('googleRecaptcha')
      if (!existingScript) {
        const script = getWindow()?.document.createElement('script')
        script.src = `https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`
        script.id = 'googleRecaptcha'
        getWindow()?.document.body.appendChild(script)

        script.onload = () => {
          if (callback) callback()
        }
      }

      if (existingScript && callback) callback()
    }

    const unload = () => {
      const existingScript = getWindow()?.document.getElementById('googleRecaptcha')
      existingScript?.remove()

      const recaptchaElems = getWindow()?.document.getElementsByClassName('grecaptcha-badge')
      if (recaptchaElems.length) {
        recaptchaElems[0].remove()
      }

      const loadedScript = getWindow()?.document.querySelector('html > script[src*="https://www.gstatic.com/recaptcha/releases/"]')
      loadedScript?.remove?.()
    }

    load(() => {
      setReadyCaptcha(true)
    })
    return () => {
      unload()
      setReadyCaptcha(false)
    }
  }, [recaptchaSiteKey])

  const getToken = useCallback(async (): Promise<string> => {
    const grecaptcha = getWindow()?.grecaptcha

    if (!grecaptcha) return
    if (!readyCaptcha) return
    if (!recaptchaSiteKey) return

    return new Promise((resolve, reject) => {
      grecaptcha?.ready(function () {
        grecaptcha
          ?.execute(recaptchaSiteKey, { action: 'submit' })
          .then(function (token) {
            resolve(token)
          })
          .catch((e) => {
            reject(e)
          })
      })
    })
  }, [readyCaptcha, recaptchaSiteKey])

  const challenge = React.useCallback(async () => {
    const token = await getToken()

    const { status } = await fetch(url, { method: 'post', body: JSON.stringify({ token }) })

    return status === 'ok'
  }, [recaptchaSiteKey, getToken])

  return { challenge, readyCaptcha, getToken }
}

export const useRecaptchaToken = (props: UseRecaptchaProps) => {
  const [token, setToken] = useState<string>(null)
  const { readyCaptcha, getToken } = useRecaptcha(props)

  useEffect(() => {
    //if the recaptcha is not ready and the there is no value for token
    if (!readyCaptcha) return

    const load = async () => {
      const recaptchaToken = await getToken()
      setToken(recaptchaToken)
    }

    load()
  }, [readyCaptcha, getToken])

  return { token, isTokenReady: !!token }
}
